// Module Import
//---------------------------------
import React, { useState, useRef } from 'react'
import styled from 'styled-components'
import ReCAPTCHA from 'react-google-recaptcha'
import { graphql, useStaticQuery } from 'gatsby'
import axios from 'axios'
import Field from '../../atoms/fields/field'
import TypographyAtom from '../../atoms/typography/typography'
import useFormValidation from '../../../hooks/useFormValidation'
import { contactFormSchema } from '../../../utils/form/validationSchema'
import ValidationWrapper from '../../atoms/fields/ValidationWrapper'
import { useToast } from '../../../context/ToastContext'
import TextareaField from '../../atoms/fields/textarea'
import validateCaptchaToken from '../../../utils/captcha/validateCaptchaToken'
import PhoneFieldWithCountry from '../../atoms/fields/PhoneField/PhoneFieldWithCountry'
import { brandIvory, brandOrange } from '../../../assets/consts/color'
import ButtonAtom from '../../atoms/buttons/buttons'

// Component Import
//---------------------------------
const Form = styled.form`
  display: flex;
  flex-direction: column;
  border-radius: 4px;
  overflow: hidden;
  gap: 8px;

  h3 {
    margin: 0;
    color: ${brandOrange};
  }

  & > h4 {
    color: ${brandOrange};
    text-align: center;
    margin-top: 30px;
  }
`

const BodyWrapper = styled.div`
  display: flex;
  padding: 20px;
  flex-direction: column;
  justify-content: flex-start;
  align-items: stretch;
  gap: 24px;
  align-self: stretch;
  background: ${brandIvory};

  @media only screen and (min-width: 48em) {
    padding: 40px;
  }

  a {
    text-decoration: none;
  }
`

const FieldWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 24px;
`

// Styled Component Declarations
//---------------------------------

// Component Export
//---------------------------------
export default function ContactForm() {
  const [isSent, setIsSent] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const recaptcha = useRef(null)
  const formRef = useRef(null)

  const { showToast } = useToast()
  const { validateValues, validateFieldOnChange, errors } = useFormValidation({
    schema: contactFormSchema,
    formRef,
  })
  const data = useStaticQuery(graphql`
    query {
      contentfulComponentConfigData {
        contactsMessageSentConfirmation
      }
    }
  `)

  const messageSentConfirmation =
    data?.contentfulComponentConfigData?.contactsMessageSentConfirmation || ''

  const handleFieldClick = () => setIsSent(false)

  const handleErrors = async ({ target: { name, value } }) => {
    await validateFieldOnChange({ name, value })
  }

  const onSubmit = async (e) => {
    e.preventDefault()
    setIsLoading(true)

    const formData = new FormData(e.target)
    formData.set('pageHref', document?.URL)

    const formValues = {
      name: formData.get('name'),
      email: formData.get('email'),
      phone: formData.get('phone'),
      clientMessage: formData.get('clientMessage'),
      pageHref: formData.get('pageHref'),
    }

    const validatedData = await validateValues(formValues)

    if (!validatedData) {
      showToast('Validation error', '')
      setIsLoading(false)
      return
    }

    const isCaptchaValid = await validateCaptchaToken(
      recaptcha.current.getValue(),
      showToast,
      setIsLoading
    )

    if (!isCaptchaValid) return

    const response = await axios
      .post(`${process.env.GATSBY_NETLIFY_API_URL}/mail_feedback`, formData)
      .catch((err) => {
        showToast(
          'Message unsuccessfully sent',
          err?.response?.data?.error || 'Failed to send email'
        )
        return null
      })

    if (response) {
      setIsSent(true)
      e.target.reset()

      if (typeof window !== 'undefined' && window.dataLayer) {
        window.dataLayer.push({
          event: 'gaTriggerEvent',
          gaEventCategory: 'form_send',
          gaEventAction: 'contact_us',
        })
      }
    }

    recaptcha?.current.reset()
    setIsLoading(false)
  }

  return (
    <Form ref={formRef} id="contactForm" onSubmit={onSubmit}>
      <BodyWrapper>
        <TypographyAtom type="H4" copy="Contact Us" weight="Bold" />
        <TypographyAtom
          type="P"
          copy="Drop us a message and we will get back to you as soon as possible"
          weight="Regular"
        />
        <FieldWrapper>
          <ValidationWrapper errorMessage={errors.name}>
            <Field
              labelText="Name"
              labelFor="nameField"
              placeholderText="Your Name"
              fieldType="text"
              fieldId="nameField"
              fieldName="name"
              onChange={handleErrors}
              onClick={handleFieldClick}
            />
          </ValidationWrapper>
          <ValidationWrapper errorMessage={errors.email}>
            <Field
              labelText="Contact Email"
              labelFor="contactEmail"
              placeholderText="Contact Email"
              fieldType="email"
              fieldId="contactEmail"
              fieldName="email"
              onChange={handleErrors}
              onClick={handleFieldClick}
            />
          </ValidationWrapper>
          <ValidationWrapper errorMessage={errors.phone}>
            <PhoneFieldWithCountry
              fieldName="phone"
              onChange={validateFieldOnChange}
              onClick={handleFieldClick}
            />
          </ValidationWrapper>
          <ValidationWrapper errorMessage={errors.clientMessage}>
            <TextareaField
              labelText="Message"
              labelFor="messageField"
              placeholderText="Message"
              fieldType="text"
              fieldId="messageField"
              fieldName="clientMessage"
              onChange={handleErrors}
              onClick={handleFieldClick}
            />
          </ValidationWrapper>
        </FieldWrapper>
        <ReCAPTCHA sitekey={process.env.GATSBY_SITE_KEY} ref={recaptcha} />
      </BodyWrapper>

      {isSent ? (
        <TypographyAtom weight="Bold" type="H4" copy={messageSentConfirmation} />
      ) : (
        <ButtonAtom
          buttonType="Submit Button"
          buttonCopy="Contact Us"
          form="contactForm"
          isLoading={isLoading}
          disabled={isLoading}
        />
      )}
    </Form>
  )
}

ContactForm.propTypes = {}
